import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Loader, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { isPlanCancelling, planEndDate, formatPlanDate } from '../../utils/planCancellation';
import { authService } from '../../services/apiService';
import {
  createBillingPortalSession,
  getPlans,
  createAiTokenCheckout,
  getInvoices,
  billingErrorMessage,
} from '../../utils/apiClient';
import { setPlanModalOpen } from '../../store/uiModalStore';
import ApibooLoader from '../ui/ApibooLoader';
import { ConfirmModal } from '../ui/Modal';
import { isTauri } from '../../utils/tauri';
import { Row, Section } from '../settingsPrimitives';
import PersonalSyncSection from './PersonalSyncSection';
import { WithdrawContractRow } from './WithdrawalSection';
import { DEFAULT_PLANS, aiAllowanceLabel } from '../../plans/planCatalog';

/* ── Billing constants & helpers ─────────────────────────────────────────────── */
export const BILLING_AI_TOPUP_PRICE_ID = process.env.REACT_APP_STRIPE_AI_TOPUP_PRICE_ID || null;

// Plans v2: the same fallback catalogue as the upgrade modal (plans/planCatalog.js).
export const BILLING_DEFAULT_PLANS = DEFAULT_PLANS;

export function billingNormalizePlan(plan) {
  return {
    id: plan.id, name: plan.name, price: plan.price,
    priceSub: plan.price_sub ?? plan.priceSub ?? '',
    description: plan.description, highlight: !!plan.highlight,
    aiTokensLabel: plan.ai_tokens_label ?? plan.aiTokensLabel ?? '',
    limits: plan.limits && typeof plan.limits === 'object' ? plan.limits : null,
    features: Array.isArray(plan.features) ? plan.features : [],
    stripePriceMonthly: plan.stripe_price_monthly_id ?? plan.stripePriceMonthly ?? null,
    stripePriceAnnual: plan.stripe_price_annual_id ?? plan.stripePriceAnnual ?? null,
  };
}

/**
 * A plan is only cancellable if money actually flows through Stripe for it.
 * Admin-granted / custom plans (created with no Stripe price) have no
 * subscription behind them, so there is nothing to cancel, resume, invoice,
 * or open a billing portal for.
 */
export function billingIsStripeBacked(plan) {
  return !!(plan?.stripePriceMonthly || plan?.stripePriceAnnual);
}

export function billingFormatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return '—'; }
}

/** Whole days between now and `iso` (rounded up). Null when unknown/past. */
export function billingDaysLeft(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  if (ms <= 0) return null;
  return Math.ceil(ms / 86400000);
}

/** "12 days left" / "1 day left" / "less than a day left" — or null when unknown. */
export function billingTimeLeftLabel(iso) {
  const days = billingDaysLeft(iso);
  if (days === null) return null;
  return days === 1 ? '1 day left' : `${days} days left`;
}

/**
 * When the current subscription started, for the withdrawal form's
 * identification block. The server's own field wins; without one, the oldest
 * invoice is the order date (the first invoice is issued at checkout).
 */
export function subscriptionStartedAt(user, invoices) {
  const own = user?.subscription_started_at || user?.plan_started_at;
  if (own) return own;
  const dates = (invoices || []).map((inv) => inv?.date).filter((d) => Number.isFinite(d));
  if (dates.length === 0) return null;
  return new Date(Math.min(...dates) * 1000).toISOString();
}

export async function billingOpenUrl(url) {
  try {
    if (isTauri()) {
      const { open } = await import('@tauri-apps/plugin-shell');
      await open(url);
    } else {
      window.location.href = url;
    }
  } catch {
    window.location.href = url;
  }
}

/**
 * Billing, for a PAYER.
 *
 * `payer` is `{ type: 'user' | 'org', ... }`. Settings passes the signed-in
 * user; an organisation passes itself. The point is that "cancel this plan"
 * is written once — a second copy for organisations would drift from this one
 * on the next Stripe change, and the drift would be invisible until somebody
 * could not cancel.
 *
 * WHAT DOES NOT FOLLOW THE PAYER: the promotion-code field. A code grants a
 * plan TO A PERSON, and `authService.redeemPromotion` posts to the user's
 * endpoint. Making it payer-scoped along with everything else would either
 * grant the plan to your personal account while you are looking at the
 * organisation's page and believe you upgraded the company, or — if somebody
 * "fixed" that by routing it through the organisation — let a personal code
 * grant a company-wide plan. Both look like they worked. Neither errors.
 *
 * So it stays user-scoped, and it does not render at all for an organisation:
 * "redeem a code for yourself" has no business on a company's billing page.
 */
// Exported for tests (planCancelDate.test.js); the modal is its only caller.
export const BillingTab = ({ showToast, payer = null }) => {
  const { t, i18n } = useTranslation();
  const { user, cancelPlan, reactivatePlan } = useAuthStore();
  // Defaulting to the user keeps every existing call site working unchanged.
  const subject = payer || { type: 'user', ...(user || {}) };
  const isUserPayer = subject.type === 'user';
  const currentPlan = subject.plan ?? 'free';
  const isPaid = currentPlan !== 'free';

  const [plans, setPlans] = useState(BILLING_DEFAULT_PLANS);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [promoError, setPromoError] = useState('');

  /**
   * Redeem a code, then re-read the user.
   *
   * The response says which plan was granted, but the user object drives every
   * limit and label in the app — so the source of truth is re-fetched rather
   * than patched locally. Patching would show "team" while the store still held
   * the old plan's limits, which reads as the grant having half-worked.
   */
  const redeemCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code || redeeming) return;
    setRedeeming(true);
    setPromoError('');
    try {
      const result = await authService.redeemPromotion(code);
      const fresh = await authService.getUser();
      useAuthStore.setState({ user: fresh });
      setPromoCode('');
      showToast?.(
        result?.plan
          ? `Code applied — you are on the ${result.plan} plan.`
          : 'Code applied.',
        'success'
      );
    } catch (err) {
      // 422 covers unknown / disabled / expired / exhausted with ONE message,
      // on purpose. Show the server's wording rather than guessing which case.
      setPromoError(
        err?.response?.data?.error || err?.message || 'That code could not be applied.'
      );
    } finally {
      setRedeeming(false);
    }
  };

  useEffect(() => {
    authService.getUser()
      .then((fresh) => useAuthStore.setState({ user: fresh }))
      .catch(() => {});
    getPlans()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          // Keep every plan, not just the catalog four — a user can sit on a
          // custom plan, and dropping it here is what made the UI fall back to
          // rendering the raw plan id (e.g. "FREEPRO") as the plan name.
          setPlans(data.map(billingNormalizePlan));
        }
      })
      .catch(() => setPlans(BILLING_DEFAULT_PLANS));
    setInvoicesLoading(true);
    getInvoices()
      .then((data) => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false));
  }, []);

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const url = await createBillingPortalSession();
      await billingOpenUrl(url);
    } catch (err) {
      showToast?.(billingErrorMessage(err, 'Failed to open billing portal. Please try again.'), 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    const result = await cancelPlan();
    setCancelling(false);
    setShowCancelConfirm(false);
    if (result.success) {
      // The store has the server's answer by now (cancel_at included).
      const endsOn = formatPlanDate(planEndDate(useAuthStore.getState().user), i18n.language);
      useNotificationStore.getState().info(`You keep full access until ${endsOn}.`, 'Subscription canceled');
    } else {
      showToast?.(result.error || 'Could not cancel your subscription. Please try again.', 'error');
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    const result = await reactivatePlan();
    setReactivating(false);
    if (result.success) {
      showToast?.('Subscription resumed. Your plan will keep renewing as usual.', 'success');
    } else {
      showToast?.(result.error || 'Could not resume your subscription. Please try again.', 'error');
    }
  };

  const handleAiTopUp = async () => {
    if (!BILLING_AI_TOPUP_PRICE_ID) return;
    try {
      const url = await createAiTokenCheckout(BILLING_AI_TOPUP_PRICE_ID);
      await billingOpenUrl(url);
    } catch (err) {
      showToast?.(billingErrorMessage(err, 'Failed to start checkout. Please try again.'), 'error');
    }
  };

  const expiresAt = user?.plan_expires_at;
  // Cancelling = `cancel_at_period_end` OR a `cancel_at` date (backend #223);
  // the plan ends on `cancel_at`, else on `plan_expires_at` (utils/planCancellation.js).
  const isCancelling = isPlanCancelling(user);
  const endsAt = isCancelling ? planEndDate(user) : expiresAt;
  const endsOnDate = formatPlanDate(endsAt, i18n.language);
  const isExpired = expiresAt && new Date(expiresAt) < new Date();
  const timeLeft = billingTimeLeftLabel(endsAt);
  const planConfig = plans.find((p) => p.id === currentPlan) || { id: currentPlan, name: currentPlan.toUpperCase() };
  // Paid-looking but not Stripe-backed = an admin-granted custom plan.
  const isBillable = isPaid && billingIsStripeBacked(planConfig);
  const isGranted = isPaid && !isBillable;
  const billingCycleLabel = user?.billing_cycle
    ? user.billing_cycle.charAt(0).toUpperCase() + user.billing_cycle.slice(1)
    : 'Monthly';

  return (
    <>
      <Section title="Current Plan">
        {(cancelling || reactivating) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'var(--surface-2, rgba(127,127,127,0.1))' }}>
            <ApibooLoader size={18} />
            <span style={{ fontSize: 12, opacity: 0.7 }}>{cancelling ? 'Canceling subscription…' : 'Resuming subscription…'}</span>
          </div>
        )}

        {isCancelling && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <AlertTriangle size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#f59e0b' }} data-testid="billing-plan-ends-on">
              <strong>{t('plans.billing.endsOn', { date: endsOnDate })}</strong>
              {timeLeft ? ` — ${timeLeft}` : ''}. {t('plans.billing.cancelKeepAccess')}
            </span>
          </div>
        )}

        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              {planConfig.name} plan{isPaid ? ` (${billingCycleLabel})` : ''}
            </div>
            <div className="settings-row-desc" style={{ marginTop: 4 }}>
              {isGranted
                ? `Granted by apiboo — no billing attached${expiresAt ? `, active until ${billingFormatDate(expiresAt)}` : ''}.`
                : isExpired
                ? 'Your plan has expired.'
                : isCancelling
                ? `Full access until ${endsOnDate}${timeLeft ? ` (${timeLeft})` : ''}, then you move to the free plan.`
                : isPaid
                ? `Renews automatically on ${billingFormatDate(expiresAt)}.`
                : 'You are on the free plan.'}
            </div>
            {aiAllowanceLabel(planConfig) && (
              <div className="settings-row-desc" style={{ marginTop: 4 }} data-testid="billing-ai-allowance">
                {aiAllowanceLabel(planConfig)}
              </div>
            )}
          </div>
          <button type="button" className="settings-action-btn" style={{ flexShrink: 0 }} onClick={() => setPlanModalOpen(true)}>
            {isPaid ? 'Adjust plan' : 'Upgrade plan'}
          </button>
        </div>

        {/* Sits under the upgrade row because a promotion code is the other way
            to change plan — it GRANTS one outright rather than discounting a
            purchase, so it belongs beside the paid route, not inside it.

            USER PAYERS ONLY. A code grants a plan to a PERSON, and this posts
            to the user's endpoint — rendering it on an organisation's billing
            page would let somebody redeem a personal code while looking at the
            company's plan and believe they had upgraded the company. */}
        {isUserPayer && (
        <div className="billing-promo">
          <div className="billing-promo-head">
            <span className="billing-promo-title">Have a code?</span>
            <span className="billing-promo-desc">
              Redeem a promotion code to activate a plan. No payment details needed.
            </span>
          </div>
          <div className="billing-promo-row">
            <input
              type="text"
              className="modal-input billing-promo-input"
              value={promoCode}
              placeholder="YOUR-CODE"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={redeeming}
              onChange={(e) => {
                setPromoCode(e.target.value);
                if (promoError) setPromoError('');
              }}
              // Enter submits: this is a one-field form, and making people
              // reach for the mouse to finish it is friction for no reason.
              onKeyDown={(e) => {
                if (e.key === 'Enter') redeemCode();
              }}
            />
            <button
              type="button"
              className="settings-action-btn"
              onClick={redeemCode}
              disabled={redeeming || !promoCode.trim()}
            >
              {redeeming && <RefreshCw size={13} className="animate-spin" aria-hidden />}
              Redeem
            </button>
          </div>
          {promoError && (
            <div className="billing-promo-error" role="alert">{promoError}</div>
          )}
        </div>
        )}

        {isBillable && (
          <Row label="My billing info" desc="Invoices, payment method, and billing details.">
            <button
              type="button"
              className="settings-action-btn"
              onClick={handleBillingPortal}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening…' : 'Open'}
            </button>
          </Row>
        )}
      </Section>
      {isUserPayer && <PersonalSyncSection />}

      {!user?.is_admin && BILLING_AI_TOPUP_PRICE_ID && (
        <Section title="AI Credits">
          <div className="settings-row" style={{ alignItems: 'flex-start' }}>
            <div className="min-w-0">
              <div className="settings-row-label">Need more AI credits?</div>
              <div className="settings-row-desc">Buy a one-time pack — tokens are added on top of your monthly allowance.</div>
            </div>
            <button type="button" className="settings-action-btn flex-shrink-0 ml-4" onClick={handleAiTopUp}>
              Buy AI credits
            </button>
          </div>
        </Section>
      )}

      {isBillable && (
        <Section title="Invoices">
          {invoicesLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', opacity: 0.7 }}>
              <span className="ring-spin" style={{ width: 15, height: 15 }} />
              <span style={{ fontSize: 13 }}>Loading invoices…</span>
            </div>
          ) : invoices.length === 0 ? (
            <div style={{ fontSize: 13, padding: '10px 0', opacity: 0.5 }}>No invoices found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border, #27272a)' }}>
                  {['Date', 'Total', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px 8px', fontWeight: 600, fontSize: 12, opacity: 0.6 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const date = new Date(inv.date * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                  const amount = (inv.amount / 100).toLocaleString('en-US', { style: 'currency', currency: (inv.currency || 'usd').toUpperCase() });
                  const status = inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : '—';
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border, #27272a)' }}>
                      <td style={{ padding: '10px 8px' }}>{date}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {amount}
                          <Info size={12} style={{ opacity: 0.4 }} />
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px' }}>{status}</td>
                      <td style={{ padding: '10px 8px' }}>
                        {inv.url ? (
                          <button
                            type="button"
                            className="settings-link-btn"
                            onClick={() => billingOpenUrl(inv.url)}
                          >
                            View
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {isBillable && (
        <Section title="Subscription">
          <Row
            label={isCancelling ? 'Resume subscription' : 'Cancel subscription'}
            desc={isCancelling
              ? `Scheduled to end on ${endsOnDate}${timeLeft ? ` — ${timeLeft}` : ''}. Resume to keep your plan renewing.`
              : `Keeps full access until ${billingFormatDate(expiresAt)}, the end of your current billing period. No further charges after that.`}
          >
            {isCancelling ? (
              <button type="button" className="settings-action-btn" onClick={handleReactivate} disabled={reactivating}>
                {reactivating ? <><Loader size={13} /> Resuming…</> : 'Resume subscription'}
              </button>
            ) : (
              <button type="button" className="btn-destructive" onClick={() => setShowCancelConfirm(true)} disabled={cancelling}>
                Cancel subscription
              </button>
            )}
          </Row>
          {/* § 13a FAGG "Vertrag widerrufen", next to Cancel (legal must-do 1).
              Shown for every paid subscription, also while it is cancelling:
              the withdrawal period does not stop because a cancel is pending,
              and the server — not this tab — decides whether it still runs.
              A person's right, so user payers only: an organisation is a
              business customer, and the endpoint withdraws the USER's contract. */}
          {isUserPayer && (
            <WithdrawContractRow
              planName={planConfig.name}
              billingCycle={user?.billing_cycle}
              email={user?.email}
              startedAt={subscriptionStartedAt(user, invoices)}
            />
          )}
        </Section>
      )}

      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
        title="Cancel your subscription?"
        message={`Your ${planConfig.name} plan stays fully active until ${billingFormatDate(expiresAt)}${timeLeft ? ` (${timeLeft})` : ''}. You won't be charged again, and after that date your account moves to the free plan. You can resume any time before then.`}
        confirmText="Cancel subscription"
        cancelText="Keep my plan"
        type="danger"
      />
    </>
  );
};
